import { Instance, SnapshotIn, types } from 'mobx-state-tree';
import { dropIgnoredAttachments } from '../utils';
import { HistoryMessageDTO } from 'http-requests/house-chats';
import { RequestStateModel } from '../../general-models';

import { ChatMessageModel } from './message';

type TChatMessageSnapshot = SnapshotIn<typeof ChatMessageModel>;

const toInstances = (items: HistoryMessageDTO[]) =>
  items.map((item) => ({
    ...item,
    attachments: dropIgnoredAttachments(item.attachments ?? []),
  })) as unknown as Instance<typeof ChatMessageModel>[];

const sortDescending = (items: HistoryMessageDTO[]): HistoryMessageDTO[] =>
  [...items].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

const messageTime = (message: { created_at: string }): number =>
  new Date(message.created_at).getTime();

export const ChatHistoryModel = types
  .model('ChatHistoryModel', {
    chatId: types.maybeNull(types.number),
    messages: types.optional(types.array(ChatMessageModel), []),
    total: types.optional(types.number, 0),
    hasOlder: types.optional(types.boolean, false),
    hasNewer: types.optional(types.boolean, false),
    firstUnreadId: types.optional(types.maybeNull(types.string), null),
    requestsState: types.optional(RequestStateModel, {}),
    loadOlderState: types.optional(RequestStateModel, {}),
    loadNewerState: types.optional(RequestStateModel, {}),
    backgroundRefreshState: types.optional(RequestStateModel, {}),
  })
  .views((self) => ({
    get newestMessage() {
      return self.messages[0] ?? null;
    },
    get oldestMessage() {
      return self.messages[self.messages.length - 1] ?? null;
    },
    hasMessage(maxMessageId: string): boolean {
      return self.messages.some((m) => m.max_message_id === maxMessageId);
    },
    findMessage(maxMessageId: string) {
      return (
        self.messages.find((m) => m.max_message_id === maxMessageId) ?? null
      );
    },
    findById(id: string) {
      return self.messages.find((m) => m.id === id) ?? null;
    },
  }))
  .actions((self) => ({
    setChatId(chatId: number) {
      self.chatId = chatId;
    },
    setLatest(items: HistoryMessageDTO[], total: number) {
      self.messages.replace(toInstances(sortDescending(items)));
      self.total = total;
      self.hasOlder = self.messages.length < total;
      self.hasNewer = false;
    },
    setUnreadBoundary(unreadCount: number) {
      if (unreadCount <= 0) {
        self.firstUnreadId = null;

        return;
      }

      let counted = 0;

      for (const message of self.messages) {
        counted += 1;

        if (counted === unreadCount) {
          self.firstUnreadId = message.max_message_id;

          return;
        }
      }

      self.firstUnreadId = self.oldestMessage?.max_message_id ?? null;
    },
    clearUnreadBoundary() {
      self.firstUnreadId = null;
    },
    appendOlderTip(items: HistoryMessageDTO[], total: number) {
      const known = new Set(self.messages.map((m) => m.max_message_id));
      const older = sortDescending(items).filter(
        (m) => !known.has(m.max_message_id)
      );

      self.messages.push(...toInstances(older));
      self.total = older.length
        ? Math.max(total, self.messages.length)
        : self.messages.length;
      self.hasOlder = self.messages.length < self.total;
    },
    jumpTo(items: HistoryMessageDTO[], total: number) {
      self.messages.replace(toInstances(sortDescending(items)));
      self.total = total;
      self.hasOlder = true;
      self.hasNewer = true;
    },
    appendOlderAnchored(items: HistoryMessageDTO[]) {
      const known = new Set(self.messages.map((m) => m.max_message_id));
      const boundary = self.oldestMessage
        ? messageTime(self.oldestMessage)
        : Infinity;
      const older = sortDescending(items).filter(
        (m) => !known.has(m.max_message_id) && messageTime(m) < boundary
      );

      if (!older.length) {
        self.hasOlder = false;

        return;
      }

      self.messages.push(...toInstances(older));
    },
    appendNewerAnchored(items: HistoryMessageDTO[]) {
      const known = new Set(self.messages.map((m) => m.max_message_id));
      const boundary = self.newestMessage
        ? messageTime(self.newestMessage)
        : -Infinity;
      const newer = sortDescending(items).filter(
        (m) => !known.has(m.max_message_id) && messageTime(m) > boundary
      );

      if (!newer.length) {
        self.hasNewer = false;

        return;
      }

      self.messages.unshift(...toInstances(newer));
    },
    addOptimistic(item: TChatMessageSnapshot) {
      self.messages.unshift(item as Instance<typeof ChatMessageModel>);
      self.total += 1;
    },
    confirmOptimistic(tempId: string, realMaxMessageId: string) {
      const optimistic = self.messages.find((m) => m.id === tempId);

      if (!optimistic) {
        return;
      }

      const echoed = self.messages.find(
        (m) => m !== optimistic && m.max_message_id === realMaxMessageId
      );

      if (echoed) {
        self.messages.remove(optimistic);
        self.total = Math.max(0, self.total - 1);

        return;
      }

      optimistic.confirmSent(realMaxMessageId);
    },
    removeById(id: string) {
      const message = self.messages.find((m) => m.id === id);

      if (message) {
        self.messages.remove(message);
        self.total = Math.max(0, self.total - 1);
      }
    },
    prependNewer(item: TChatMessageSnapshot) {
      if (
        self.hasNewer ||
        self.messages.some((m) => m.max_message_id === item.max_message_id)
      ) {
        return;
      }

      self.messages.unshift(item as Instance<typeof ChatMessageModel>);
      self.total += 1;
    },
    mergeLatest(items: HistoryMessageDTO[], total: number) {
      if (self.hasNewer) {
        return;
      }

      const known = new Set(self.messages.map((m) => m.max_message_id));
      const newestTime = self.newestMessage
        ? messageTime(self.newestMessage)
        : -Infinity;
      const fresh = sortDescending(items).filter(
        (m) => !known.has(m.max_message_id) && messageTime(m) > newestTime
      );

      if (fresh.length) {
        self.messages.unshift(...toInstances(fresh));
      }

      self.total = Math.max(total, self.messages.length);
    },
  }));

export type TChatHistory = Instance<typeof ChatHistoryModel>;
